import { Shield, Lock, EyeOff, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface TrustSectionProps {
  className?: string;
  variant?: 'light' | 'dark';
}

/**
 * TrustSection - Privacy and security trust badges component
 *
 * Displays 4 badges communicating:
 * 1. End-to-end encryption
 * 2. Data ownership (only the organization has access)
 * 3. Zero access (Sloty cannot see sensitive data)
 * 4. GDPR compliance
 */
export const TrustSection = ({ className = '', variant = 'light' }: TrustSectionProps) => {
  const { t } = useTranslation('landing-common');

  const bgClass = variant === 'dark' ? 'bg-slate-50' : 'bg-white';

  const badges = [
    {
      icon: Lock,
      titleKey: 'privacy.badges.encryption.title',
      descriptionKey: 'privacy.badges.encryption.description',
    },
    {
      icon: Shield,
      titleKey: 'privacy.badges.yourAccess.title',
      descriptionKey: 'privacy.badges.yourAccess.description',
    },
    {
      icon: EyeOff,
      titleKey: 'privacy.badges.zeroAccess.title',
      descriptionKey: 'privacy.badges.zeroAccess.description',
    },
    {
      icon: CheckCircle,
      titleKey: 'privacy.badges.gdpr.title',
      descriptionKey: 'privacy.badges.gdpr.description',
    },
  ];

  return (
    <section className={`py-16 sm:py-20 ${bgClass} ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-slate-900 mb-4">
            {t('privacy.title')}
          </h2>
          <p className="text-lg text-slate-700 max-w-2xl mx-auto">
            {t('privacy.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          {badges.map((badge, index) => {
            const Icon = badge.icon;
            return (
              <div
                key={index}
                className="text-center p-6 rounded-2xl bg-white shadow-xl shadow-slate-900/10"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 bg-brand-100 rounded-xl mb-4">
                  <Icon className="w-7 h-7 text-brand-600" />
                </div>
                <h3 className="text-base font-semibold text-slate-900 mb-2">
                  {t(badge.titleKey)}
                </h3>
                <p className="text-sm text-slate-600">
                  {t(badge.descriptionKey)}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default TrustSection;
